/**
 * Notification service — manages in-app notification feed via activityLog collection
 */
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sanitizeString } from '../utils/sanitize';
import { createLogger } from '../utils/logger';

const log = createLogger('notificationService');

const ACTIVITY_LOG_COLLECTION = 'activityLog';

/**
 * Create a notification (fire-and-forget — errors are logged, never thrown)
 * @param {Object} params
 * @param {string} params.userId - Recipient UID
 * @param {string} params.type - Notification type constant
 * @param {string} params.title - Short title
 * @param {string} params.message - Description text
 * @param {string} params.link - Route path to navigate on click
 */
export const createNotification = async ({ userId, type, title, message, link }) => {
  try {
    await addDoc(collection(db, ACTIVITY_LOG_COLLECTION), {
      userId,
      type,
      title: sanitizeString(title, 200),
      message: sanitizeString(message, 500),
      link: link || '/',
      read: false,
      createdAt: serverTimestamp(),
    });
    log.info('Notification created', { userId, type });
  } catch (err) {
    log.warn('Failed to create notification', { userId, type, error: err.message });
  }
};

/**
 * Mark a single notification as read
 * @param {string} notificationId
 */
export const markAsRead = async (notificationId) => {
  await updateDoc(doc(db, ACTIVITY_LOG_COLLECTION, notificationId), { read: true });
  log.info('Notification marked as read', { notificationId });
};

/**
 * Mark all notifications as read for a user
 * @param {string} userId
 */
export const markAllAsRead = async (userId) => {
  // Query all user notifications — can't filter on read==false because
  // some docs may have a missing read field (treated as unread in the UI)
  const q = query(
    collection(db, ACTIVITY_LOG_COLLECTION),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  const unread = snap.docs.filter((d) => !d.data().read);
  if (unread.length === 0) return;

  const batch = writeBatch(db);
  unread.forEach((d) => {
    batch.update(d.ref, { read: true });
  });
  await batch.commit();
  log.info('All notifications marked as read', { userId, count: unread.length });
};

/**
 * Get notifications for a user (one-time fetch)
 * @param {string} userId
 * @param {number} [limitCount=50]
 * @returns {Promise<Array>}
 */
export const getNotifications = async (userId, limitCount = 50) => {
  const q = query(
    collection(db, ACTIVITY_LOG_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};
